import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { AlertCircle, ShieldAlert, CheckCircle2 } from 'lucide-react';

export function AgreementDialog({
  open,
  onOpenChange,
  onAccept,
  workType,
  amount,
}) {
  const [agreed, setAgreed] = useState(false);

  const handleAccept = () => {
    if (agreed) {
      onAccept();
      setAgreed(false); // Reset for next time
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-card border border-border shadow-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <ShieldAlert className="w-6 h-6 text-primary-foreground" />
            </div>
            <DialogTitle className="text-2xl text-foreground font-semibold">
              Terms and Agreement
            </DialogTitle>
          </div>
          <DialogDescription className="text-base text-muted-foreground">
            Please read and accept the following terms before submitting your {workType} request
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="bg-primary p-5 rounded-xl text-primary-foreground shadow-sm">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm opacity-90">Work Type</p>
                <p className="text-xl font-semibold capitalize">{workType}</p>
              </div>
              <div className="text-right">
                <p className="text-sm opacity-90">Amount to be Paid</p>
                <p className="text-3xl font-bold">₹{amount}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              <h4 className="text-lg font-semibold text-destructive">Important Rules and Regulations</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex gap-3 p-4 bg-accent border border-border rounded-xl">
                <div className="w-8 h-8 bg-destructive rounded-full flex items-center justify-center flex-shrink-0 text-destructive-foreground font-bold text-sm">
                  1
                </div>
                <p className="text-sm text-foreground flex-1">
                  Once you make the payment, the amount will <span className="font-semibold text-destructive">NOT be refunded</span> under any circumstances.
                </p>
              </div>

              <div className="flex gap-3 p-4 bg-muted border border-border rounded-xl">
                <div className="w-8 h-8 bg-secondary text-secondary-foreground rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                  2
                </div>
                <p className="text-sm text-foreground flex-1">
                  If anyone from your home, parents, relatives, or guardians complains about us, we will not take any responsibility. You are completing and submitting your assignments with your own responsibility and awareness.
                </p>
              </div>

              <div className="p-5 bg-accent border border-border rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1">Important Notice</p>
                    <p className="text-sm text-muted-foreground">
                      By accepting these terms, you acknowledge that you are using this service voluntarily and take full responsibility for your decision.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3 pt-4 border-t border-border p-4 bg-emerald-50 dark:bg-emerald-950/20 rounded-xl">
            <Checkbox
              id="agreement"
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked)}
              className="mt-1 border-emerald-500 data-[state=checked]:bg-emerald-500"
            />
            <Label 
              htmlFor="agreement" 
              className="cursor-pointer text-sm leading-relaxed text-foreground"
            >
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span>
                  I have read and understood all the rules and regulations mentioned above. I agree to all terms and conditions and take full responsibility for my decision to use this service.
                </span>
              </div>
            </Label>
          </div>
        </div>

        <DialogFooter className="gap-3">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              setAgreed(false);
            }}
            className="border border-border hover:bg-accent"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAccept}
            disabled={!agreed}
            className="bg-emerald-600 hover:bg-emerald-700 text-white border-0 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Accept and Submit ✓
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}